import React, { useContext, useMemo, useRef, useState } from 'react';
import formatCurrency, {
  shortCurrencyFormatter,
} from '../../utils/formatCurrency';
import useOutsideClick from '../../hooks/useOutsideClick';
import mls_property_types from '../../constants/mls_property_types.json';
import mls_categories from '../../constants/mls_categories.json';
import mls_listing_statuses from '../../constants/mls_listing_statuses.json';
import { MLSMapContext } from '../../context/MLSMapContext';
import WindowQuery from '../../utils/WindowQuery';
import useWindowSize from '../../hooks/useWindowSize';

const MLSMapMarkerComponent = ({
  data,
  highlightedMarkerID,
  setListingURL,
}) => {
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const updateQuery = new WindowQuery();

  const ref = useRef();

  const [active, setActive] = useState(false);

  // Check for 'Commercial for Lease' or 'Rental'
  const isRental = data.property_type === 'G' || data.property_type === 'F';

  useOutsideClick(ref, true, () => {
    setActive(false);
  });

  const propertyType = useMemo(
    () => mls_property_types.find(c => c.value === data.property_type),
    [data]
  );
  const subType = useMemo(
    () => mls_categories.find(c => c.value === data.sub_type),
    [data]
  );
  const listingStatus = useMemo(
    () =>
      mls_listing_statuses.find(s => {
        if (isRental) {
          return s.value === `${data.listing_status}-Rental`;
        }
        return s.value === data.listing_status;
      }),
    [data.listing_status, isRental]
  );

  return (
    <a
      ref={ref}
      role="presentation"
      className="mls-marker"
      onMouseOver={() => {
        if (!isMobile) setActive(true);
      }}
      onMouseOut={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      onClick={e => {
        if (!active) {
          e.preventDefault();
          setActive(true);
        } else {
          setListingURL(
            window.Routes.mls_address_show_path(data.address_show_url)
          );
          updateQuery.push(
            window.location.href,
            `${window.Routes.mls_address_show_path(data.address_show_url)}`
          );
        }
      }}
    >
      <div
        className={`mls-price-marker ${data.listing_status
          .toLowerCase()
          .replace(/\s/g, '-')} ${
          data.id === highlightedMarkerID ? 'highlighted' : ''
        }`}
      >
        <p>
          $
          {shortCurrencyFormatter(
            Math.round(data.sold_price ?? data.list_price / 1000) * 1000,
            1
          )}
        </p>
      </div>

      {active && (
        <aside className="mls-active-marker">
          <div className="mls-marker-details">
            {data.sub_type ? (
              <p className="sub-type">
                {subType ? subType.short_label : data.sub_type}
              </p>
            ) : (
              <p className="sub-type">
                {propertyType ? propertyType.label : data.property_type}
              </p>
            )}
            <h3 className="list-price">
              {formatCurrency(data.sold_price ?? data.list_price)}
            </h3>
            {(data.total_bedrooms || data.total_baths) && (
              <p className="detail">
                {data.total_bedrooms || 0} BR {data.total_baths || 0} BA
              </p>
            )}
            {data.apx_sqft && (
              <p className="detail">{data.apx_sqft.toLocaleString()} SF</p>
            )}
            {data.acreage && !data.apx_sqft && (
              <p className="detail">{data.acreage} Acres</p>
            )}
          </div>

          <div className="mls-marker-image-wrapper">
            <img
              data-src={data.feature_image_url}
              alt=""
              className="lazyload"
            />

            <div className="image-overlay" />

            <div className="mls-marker-status">
              <p>
                <span
                  className={`mls-status ${data.listing_status.toLowerCase()}`}
                />
                <span>{listingStatus.label}</span>
              </p>
            </div>
          </div>
        </aside>
      )}
    </a>
  );
};

const MLSMapMarker = ({ data }) => {
  const { highlightedMarkerID, setListingURL } = useContext(MLSMapContext);

  return (
    <MLSMapMarkerComponent
      data={data}
      highlightedMarkerID={highlightedMarkerID}
      setListingURL={setListingURL}
    />
  );
};

export default React.memo(MLSMapMarker);

const MemoizedMLSMapMarkerComponent = ({ data }) => {
  const { setListingURL } = useContext(MLSMapContext);

  return useMemo(
    () => <MLSMapMarkerComponent data={data} setListingURL={setListingURL} />,
    [data, setListingURL]
  );
};

export const MemoizedMLSMapMarker = React.memo(MemoizedMLSMapMarkerComponent);
